/**
 * Settings Hooks
 * React Query hooks for settings-related operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetch2FAStatus,
  initiate2FASetup,
  verify2FA,
  disable2FA,
  changePassword,
  fetchActiveSessions,
  terminateSession,
  fetchKYCStatus,
  submitKYC,
} from '@/services/api/settingsService';
import { useToast } from './useToast';

/**
 * Hook to fetch 2FA status
 */
export const use2FAStatus = () => {
  return useQuery({
    queryKey: ['2faStatus'],
    queryFn: fetch2FAStatus,
    staleTime: 60000, // 1 minute
    retry: 1,
  });
};

/**
 * Hook to initiate 2FA setup
 */
export const useInitiate2FA = () => {
  const { showError } = useToast();

  return useMutation({
    mutationFn: initiate2FASetup,
    onError: (error) => {
      console.error('Error initiating 2FA:', error);
      showError('Failed to setup 2FA');
    },
  });
};

/**
 * Hook to verify 2FA
 */
export const useVerify2FA = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: verify2FA,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['2faStatus'] });
      showSuccess('2FA enabled successfully!');
    },
    onError: (error) => {
      console.error('Error verifying 2FA:', error);
      showError('Invalid verification code');
    },
  });
};

/**
 * Hook to disable 2FA
 */
export const useDisable2FA = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: disable2FA,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['2faStatus'] });
      showSuccess('2FA disabled successfully!');
    },
    onError: (error) => {
      console.error('Error disabling 2FA:', error);
      showError('Invalid verification code');
    },
  });
};

/**
 * Hook to change password
 */
export const useChangePassword = () => {
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: ({ oldPassword, newPassword }) => changePassword(oldPassword, newPassword),
    onSuccess: () => {
      showSuccess('Password changed successfully!');
    },
    onError: (error) => {
      console.error('Error changing password:', error);
      const errorMessage = error.response?.data?.message || 'Failed to change password';
      showError(errorMessage);
    },
  });
};

/**
 * Hook to fetch active sessions
 */
export const useActiveSessions = () => {
  return useQuery({
    queryKey: ['activeSessions'],
    queryFn: fetchActiveSessions,
    staleTime: 30000, // 30 seconds
    retry: 1,
  });
};

/**
 * Hook to terminate session
 */
export const useTerminateSession = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: terminateSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeSessions'] });
      showSuccess('Session terminated successfully');
    },
    onError: (error) => {
      console.error('Error terminating session:', error);
      showError('Failed to terminate session');
    },
  });
};

/**
 * Hook to fetch KYC status
 */
export const useKYCStatus = () => {
  return useQuery({
    queryKey: ['kycStatus'],
    queryFn: fetchKYCStatus,
    staleTime: 30000, // 30 seconds
    retry: 1,
    refetchInterval: (query) => {
      // Auto-refetch if status is Pending
      const status = query.state.data?.status;
      return status === 'Pending' ? 30000 : false;
    },
  });
};

/**
 * Hook to submit KYC
 */
export const useSubmitKYC = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: submitKYC,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kycStatus'] });
      showSuccess('KYC submitted successfully');
    },
    onError: (error) => {
      console.error('Error submitting KYC:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to submit KYC information';
      showError(errorMessage);
      throw error; // Re-throw to handle in component
    },
  });
};
